/**
 * consumeSSE — reads a streaming SSE response, parses each RuntimeEvent,
 * and calls onEvent for each one. Handles incomplete lines across chunks.
 */

import type { RuntimeEvent } from '../types';

export async function consumeSSE(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: RuntimeEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const reader = body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';

  try {
    while (true) {
      if (signal?.aborted) break;

      const { done, value } = await reader.read();
      if (done) break;

      buf += value;
      const lines = buf.split('\n');
      // Last element may be an incomplete line — hold it
      buf = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw || raw === '[DONE]') continue;

        try {
          const event = JSON.parse(raw) as RuntimeEvent;
          onEvent(event);
        } catch {
          // Ignore malformed frames
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
